import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { AuthLayout } from 'components/layout/AuthLayout';
import { Input, Button, Checkbox, Card, CardBody } from 'components/ui';
import { ResetPassword } from 'types/index';
import { validators } from 'utils/validators';
import { ROUTES } from 'constants/index';
import logo from '../../assets/images/logo.png';

export const ForgotPasswordPage: React.FC = () => {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<ResetPassword>();

  const { changePassword } = useAuth();
  const { toast } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
   
useEffect(() => {
    if(!id){
        navigate('/login');
    }
}, [id]);

  const onSubmit = async (data: ResetPassword) => {
    setIsLoading(true);
    try {
    if(id){
      await changePassword(id.toString(), data.password);
      toast.success('Password reset successful! You can now log in.');
      navigate(ROUTES.LOGIN, { replace: true });
    }
    } catch (error: any) {
      toast.error(error?.message || 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card>
        <CardBody className="p-4 sm:p-6">
          <div className="text-center mb-6 sm:mb-8">
            <img
              src={logo}
              alt="App Logo"
              className="mx-auto mb-3 sm:mb-4 w-32 h-32 sm:w-40 sm:h-40 object-contain"
            /> 
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
            <Input
              label="New Password"
              type="password"
              placeholder="Enter your new password"
              fullWidth
              autoFocus
              error={errors.password?.message}
              leftIcon={
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              }
              {...register('password', { required: validators.required('password'),
                validate: validators.minLength(6), })}
            />

            <Input
              label="Confirm Password"
              type="password"
              placeholder="Confirm your new password"
              fullWidth
              error={errors.confirmPassword?.message}
              leftIcon={
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              }
              {...register('confirmPassword', {
                required: validators.required('confirmPassword'),
                validate: (value) =>
                    value === getValues('password') || "Passwords do not match",
              })}
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-6 sm:mt-8"
              fullWidth
              isLoading={isLoading}
              disabled={isLoading}
            >
              Submit
            </Button>
          </form>
        </CardBody>
      </Card>
    </AuthLayout>
  );
};


